import { useErrorHandlingSubscription } from '@backoffice-frontend/data-fetching'
import {
  formatApproximateDifferenceInHoursAndMinutes,
  usePoolingReasonTranslations,
} from '@backoffice-frontend/fleet-maps-common'
import { NotInServiceReason } from '@backoffice-frontend/graphql'
import { FccAdConnectivityState } from '@backoffice-frontend/graphql/subscription'

import { usePrevious } from '@backoffice-frontend/hooks'
import { Badge, LoadingIndicator, Text } from '@moia-dev/pace-web'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CardRow } from '../CardRow'
import { CardTable } from '../CardTable'
import { CollapsibleCard } from '../CollapsibleCard'
import type { BookabilityCardVehicleFragment } from './BookabilityCard.hook'
import { useBookabilityCardVehicleTelemetrySubscription } from './BookabilityCard.subscription.hook'

const textStyles = {
  minWidth: 60,
  textWrap: 'nowrap',
} as const

export const BookabilityCard = ({
  vehicle,
  className,
}: {
  vehicle: BookabilityCardVehicleFragment
  className?: string
}) => {
  const { t } = useTranslation()
  const translatePoolingReason = usePoolingReasonTranslations()
  const [isOpen, setIsOpen] = useState(false)

  const { data, loading } = useErrorHandlingSubscription(
    useBookabilityCardVehicleTelemetrySubscription,
    {
      variables: { vehicleId: vehicle.id },
      fetchPolicy: 'no-cache',
      onError: e => {
        console.error('Error in subscription', e)
      },
    },
  )

  const missionConnectivity = data?.fccVehicleTelemetry.missionConnectivity

  const isBookable = loading ? undefined : vehicle.inService
  const prevIsBookable = usePrevious(isBookable)

  useEffect(() => {
    // Open a closed card when bookability changes from bookable to non-bookable
    if (!isOpen && !isBookable && isBookable !== prevIsBookable) {
      setIsOpen(true)
    }
  }, [isOpen, isBookable, prevIsBookable])

  return (
    <CollapsibleCard
      className={className}
      disabled={loading}
      title={t('common_bookability_label')}
      headerSlot={
        loading ? (
          <LoadingIndicator />
        ) : isBookable ? (
          <Badge label={t('common_bookable_label')} variant="success" />
        ) : (
          <Badge label={t('common_notBookable_label')} variant="critical" />
        )
      }
      open={isOpen}
      onOpenChange={v => setIsOpen(v)}
    >
      <CardTable>
        <CardRow
          label={t('common_pooling_label')}
          description={
            vehicle.pooling?.reason
              ? translatePoolingReason(vehicle.pooling.reason)
              : undefined
          }
          state={
            vehicle.pooling?.enabled ? (
              <Text css={textStyles}>{t('common_poolingOn_label')}</Text>
            ) : (
              <Badge label={t('common_poolingOff_label')} variant="critical" />
            )
          }
          updatedAt={
            vehicle?.pooling?.enabledUpdatedAt
              ? formatApproximateDifferenceInHoursAndMinutes(
                  new Date(vehicle?.pooling?.enabledUpdatedAt),
                  new Date(),
                )
              : undefined
          }
        />
        <CardRow
          label={t('adFleetMap_sdsCardSdsConnectivity_label')}
          state={
            missionConnectivity?.connectivityState ===
            FccAdConnectivityState.Connected ? (
              <Text css={textStyles}>{t('common_online_label')}</Text>
            ) : (
              <Badge label={t('common_offline_label')} variant="critical" />
            )
          }
          updatedAt={
            missionConnectivity?.timestamp
              ? formatApproximateDifferenceInHoursAndMinutes(
                  new Date(missionConnectivity.timestamp),
                  new Date(),
                )
              : undefined
          }
        />
        {vehicle.notInServiceReasons?.includes(
          NotInServiceReason.NoRecentValidLocation,
        ) && (
          <CardRow
            label={t('adFleetMap_bookabilityCard_location_label')}
            state={
              <Text
                css={theme => ({
                  ...textStyles,
                  color: theme.semantic.ColorCriticalDefault,
                })}
              >
                {t('adFleetMap_bookabilityCard_noValidlocation_label')}
              </Text>
            }
            updatedAt={t('common_notApplicable_label')}
          />
        )}
        {vehicle.notInServiceReasons?.includes(
          NotInServiceReason.OutdatedHubReturn,
        ) && (
          <CardRow
            label={t('adFleetMap_bookabilityCard_hubReturn_label')}
            state={
              <Text
                css={theme => ({
                  ...textStyles,
                  color: theme.semantic.ColorCriticalDefault,
                })}
              >
                {t('adFleetMap_bookabilityCard_outdatedHubReturn_label')}
              </Text>
            }
            updatedAt={t('common_notApplicable_label')}
          />
        )}
        {vehicle.notInServiceReasons?.includes(
          NotInServiceReason.Unresponsive,
        ) && (
          <CardRow
            label={t('common_mission_label')}
            state={
              <Text
                css={theme => ({
                  ...textStyles,
                  color: theme.semantic.ColorCriticalDefault,
                })}
              >
                {t('adFleetMap_bookabilityCard_unresponsive_label')}
              </Text>
            }
            updatedAt={t('common_notApplicable_label')}
          />
        )}
      </CardTable>
    </CollapsibleCard>
  )
}
